// Sentry ErrorBoundary wrapper for React applications.
// This component catches JavaScript errors anywhere in its child component tree,
// logs those errors to Sentry, and displays a fallback UI instead of the crashed component tree.
//
// The fallback UI can be customized as needed. Here, it simply prompts the user to reload the page.
//
// Usage:
// <ErrorBoundary>
//   <App />
// </ErrorBoundary>

import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import React, { ReactNode } from 'react';

// Props for the custom ErrorBoundary wrapper
interface ErrorBoundaryProps {
    children: ReactNode;
}

// Fallback UI shown when an error is caught by the boundary
const FallbackComponent = () => (
    <div style={{ padding: 32, textAlign: 'center' }}>
        <h1>Something went wrong</h1>
        <p>An unexpected error occurred. Please reload the page or contact support.</p>
        <button onClick={() => window.location.reload()}>Reload Page</button>
    </div>
);

// ErrorBoundary component that wraps children with Sentry's ErrorBoundary
export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
    return (
        <SentryErrorBoundary fallback={FallbackComponent}>
            {children}
        </SentryErrorBoundary>
    );
};
